import { formatFileSize, formatFileTypes, getUniqueFiles } from '@vizit/utils';
import * as React from 'react';

enum FileErrorStrings {
  TypeError = 'File must be in $fileFormats format.',
  TooLargeError = 'File must be less than $maxFileSize.',
  TooSmallError = 'File must be larger than $minFileSize.',
}

interface DropZoneProviderProps {
  /**
   * Specifies what mime type of files are allowed.
   *
   * @optional
   * @default ['image/png, 'image/jpeg']
   */
  acceptedFileTypes?: string[];
  /**
   * Whether multiple files can be selected.
   *
   * @optional
   * @default true
   * @type boolean
   */
  allowsMultiple?: boolean;
  /**
   * The children of the DropZone.
   *
   * @optional
   * @type React.ReactNode
   */
  children: React.ReactNode;
  /**
   * The default file to be uploaded.
   *
   * @optional
   * @type File
   */
  defaultFiles?: File[];
  /**
   * The maximum number of files that can be dropped.
   *
   * @optional
   * @default 100
   * @type number
   */
  maxFiles?: number;
  /**
   * The maximum size of the files to be uploaded.
   *
   * @optional
   * @default "25MB"
   * @type number
   */
  maxFileSize?: number;
  /**
   * The minimum size of the files to be uploaded.
   *
   * @optional
   * @default 2KB
   * @type number
   */
  minFileSize?: number;
  /**
   * Callback fired when files are dropped or removed.
   */
  onFilesChange?: (files: File[]) => void;
  /**
   * The function to call when an error occurs.
   *
   * @optional
   * @type (error: string) => void
   */
  onFileValidationError?: (error?: string) => void;
}

interface DropZoneContextType extends Omit<DropZoneProviderProps, 'children'> {
  /**
   * The files selected for upload.
   */
  droppedFiles: File[];
  /**
   * The errors that occurred while uploading the files.
   */
  fileErrors: Set<string>;
  /**
   * Adds an error to the file errors.
   */
  addError: (error: string) => void;
  /**
   * Resets the file errors.
   */
  resetErrors: () => void;
  /**
   * Adds files to the selected files.
   */
  addFiles: (files: File[]) => void;
  /**
   * Removes a file from the selected files.
   */
  removeFile: (file: File) => void;
}

const DropZoneContext = React.createContext<DropZoneContextType | undefined>(undefined);

function DropZoneProvider(props: DropZoneProviderProps) {
  const {
    children,
    acceptedFileTypes = ['image/png', 'image/jpeg', 'image/webp'],
    allowsMultiple = true,
    defaultFiles: defaultFilesProp = [],
    maxFiles = 100,
    maxFileSize = 25 * 1024 * 1024, // 25MB
    minFileSize = 2 * 1024, // 2KB
    onFilesChange,
    onFileValidationError,
  } = props;

  const inferredMaxFiles = allowsMultiple ? maxFiles : 1;

  // If the dropzone is not allowing multiple files, and there are more than one file in the defaultFiles,
  // then only use the first file. This will prevent weird layout issues.
  const defaultFiles = !allowsMultiple && defaultFilesProp.length > 1 ? [defaultFilesProp[0]] : defaultFilesProp;

  const [droppedFiles, setDroppedFiles] = React.useState<File[]>(defaultFiles);
  const [fileErrors, setFileErrors] = React.useState<Set<string>>(new Set());

  const addError = React.useCallback(
    (error: string) => {
      setFileErrors((prev) => new Set(prev).add(error));

      onFileValidationError?.(error);
    },
    [onFileValidationError],
  );

  const resetErrors = React.useCallback(() => {
    setFileErrors(new Set());

    onFileValidationError?.();
  }, [onFileValidationError]);

  const getValidFiles = React.useCallback(
    (files: File[]) => {
      // Filter out files that are the same file name and size from the list of existing files
      const uniqueFiles = getUniqueFiles(files, droppedFiles);

      return uniqueFiles.reduce((files: File[], currentFile: File) => {
        // Reject files that are not of in the acceptedFileTypes array
        if (acceptedFileTypes && !acceptedFileTypes.includes(currentFile.type)) {
          const acceptedFileTypesString = formatFileTypes(acceptedFileTypes);
          const error = FileErrorStrings.TypeError.replace('$fileFormats', acceptedFileTypesString);

          addError(error);

          return files;
        }

        // Reject files less than 2kb
        if (currentFile.size < minFileSize) {
          addError(FileErrorStrings.TooSmallError.replace('$minFileSize', formatFileSize(minFileSize)));

          return files;
        }

        // Reject files larger than the maxFileSize
        if (currentFile.size > maxFileSize) {
          addError(FileErrorStrings.TooLargeError.replace('$maxFileSize', formatFileSize(maxFileSize)));

          return files;
        }

        // Add the file to the list of files
        return files.concat(currentFile);
      }, [] as File[]);
    },
    [acceptedFileTypes, droppedFiles, maxFileSize, minFileSize, addError],
  );

  const addFiles = React.useCallback(
    (newFiles: File[]) => {
      // Reject if the number of files exceeds the maxFiles
      if (droppedFiles.length >= inferredMaxFiles || newFiles.length > inferredMaxFiles) {
        addError(
          `Too many files, there is a max of ${inferredMaxFiles === 1 ? '1 file' : `${inferredMaxFiles} files`} uploaded at a time`,
        );
        return;
      }

      // Filter out files that are not valid and show errors
      const validFiles = getValidFiles(newFiles);

      // Add the valid files to the list of files
      const allFiles = [...droppedFiles, ...validFiles];
      setDroppedFiles(allFiles);
      onFilesChange?.(allFiles);
    },
    [addError, inferredMaxFiles, getValidFiles, droppedFiles, onFilesChange],
  );

  const removeFile = React.useCallback(
    (file: File) => {
      // Reset the file errors
      resetErrors();

      // Filter out the file from the list of dropped files
      const filteredFiles = droppedFiles.filter((f) => f !== file);

      // Update the list of dropped files
      setDroppedFiles(filteredFiles);
      onFilesChange?.(filteredFiles);
    },
    [onFilesChange, resetErrors, droppedFiles],
  );

  return (
    <DropZoneContext.Provider
      value={{
        acceptedFileTypes,
        allowsMultiple,
        defaultFiles,
        maxFiles,
        maxFileSize,
        droppedFiles,
        fileErrors,
        addFiles,
        addError,
        resetErrors,
        removeFile,
        onFilesChange,
      }}
    >
      {children}
    </DropZoneContext.Provider>
  );
}

function useDropZone() {
  const context = React.useContext(DropZoneContext);

  if (context === undefined) {
    throw new Error('useDropZoneFile must be used within a DropZoneProvider');
  }

  return context;
}

export { DropZoneProvider, useDropZone, DropZoneContext };
export type { DropZoneProviderProps };
